import { useState, useEffect, useCallback } from 'react';
import { Evolutivo } from '../types';
import { getEvolutivos, getEvolutivosByPatientId, createEvolutivo, updateEvolutivo, deleteEvolutivo } from '../services/evolutivoService';

interface UseEvolutivosReturn {
  evolutivos: Evolutivo[];
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  create: (data: Omit<Evolutivo, 'id' | 'userId' | 'createdAt' | 'updatedAt'>) => Promise<Evolutivo | null>;
  update: (id: string, data: Partial<Omit<Evolutivo, 'id' | 'userId' | 'createdAt' | 'updatedAt' | 'patientId'>>) => Promise<Evolutivo | null>;
  remove: (id: string) => Promise<boolean>;
}

/**
 * Hook para gestionar evolutivos (todos del usuario)
 */
export const useEvolutivos = (): UseEvolutivosReturn => {
  const [evolutivos, setEvolutivos] = useState<Evolutivo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getEvolutivos();
      setEvolutivos(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al cargar evolutivos');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const create = useCallback(async (data: Omit<Evolutivo, 'id' | 'userId' | 'createdAt' | 'updatedAt'>) => {
    const result = await createEvolutivo(data);
    if (result) {
      await refresh();
    }
    return result;
  }, [refresh]);

  const update = useCallback(async (id: string, data: Partial<Omit<Evolutivo, 'id' | 'userId' | 'createdAt' | 'updatedAt' | 'patientId'>>) => {
    const result = await updateEvolutivo(id, data);
    if (result) {
      await refresh();
    }
    return result;
  }, [refresh]);

  const remove = useCallback(async (id: string) => {
    const success = await deleteEvolutivo(id);
    if (success) {
      await refresh();
    }
    return success;
  }, [refresh]);

  return {
    evolutivos,
    loading,
    error,
    refresh,
    create,
    update,
    remove
  };
};

/**
 * Hook para gestionar evolutivos de un paciente específico
 */
export const usePatientEvolutivos = (patientId: string | null): UseEvolutivosReturn => {
  const [evolutivos, setEvolutivos] = useState<Evolutivo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!patientId) {
      setEvolutivos([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const data = await getEvolutivosByPatientId(patientId);
      setEvolutivos(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al cargar evolutivos del paciente');
    } finally {
      setLoading(false);
    }
  }, [patientId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const create = useCallback(async (data: Omit<Evolutivo, 'id' | 'userId' | 'createdAt' | 'updatedAt'>) => {
    const result = await createEvolutivo(data);
    if (result) {
      await refresh();
    }
    return result;
  }, [refresh]);

  const update = useCallback(async (id: string, data: Partial<Omit<Evolutivo, 'id' | 'userId' | 'createdAt' | 'updatedAt' | 'patientId'>>) => {
    const result = await updateEvolutivo(id, data);
    if (result) {
      await refresh();
    }
    return result;
  }, [refresh]);

  const remove = useCallback(async (id: string) => {
    const success = await deleteEvolutivo(id);
    if (success) {
      await refresh();
    }
    return success;
  }, [refresh]);

  return {
    evolutivos,
    loading,
    error,
    refresh,
    create,
    update,
    remove
  };
};
